export const RECORD_DURATION_MS = 10000;
export const WATERMARK = "Made with Fishy – swimfishy.com";
const FPS = 30;
const MAX_WIDTH = 1280;

const MIME_CANDIDATES = [
    "video/mp4;codecs=avc1",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
];

export function pickMimeType() {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") return null;
    const isSupported = window.MediaRecorder.isTypeSupported?.bind(window.MediaRecorder);
    if (!isSupported) return "";
    return MIME_CANDIDATES.find((type) => isSupported(type)) ?? null;
}

export function isRecordingSupported() {
    if (typeof document === "undefined") return false;
    const canvas = document.createElement("canvas");
    return typeof canvas.captureStream === "function" && pickMimeType() !== null;
}

export function extensionFor(mimeType) {
    return mimeType && mimeType.startsWith("video/mp4") ? "mp4" : "webm";
}

function loadImage(src) {
    return new Promise((resolve) => {
        if (!src) return resolve(null);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

function cssUrl(value) {
    const match = /url\(["']?([^"')]+)["']?\)/.exec(value || "");
    return match ? match[1] : null;
}

// Pre-render a fish with its mask applied, so each frame is a single drawImage.
async function prepareFishSprite(imgEl) {
    const [img, mask] = await Promise.all([
        loadImage(imgEl.currentSrc || imgEl.src),
        loadImage(cssUrl(imgEl.style.webkitMaskImage || imgEl.style.WebkitMaskImage)),
    ]);
    if (!img) return null;
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    if (mask) {
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(mask, 0, 0, canvas.width, canvas.height);
    }
    return canvas;
}

// Fish are flipped with CSS transforms/scale on ancestors; work out the net horizontal sign.
function horizontalSign(el, root) {
    let sign = 1;
    for (let node = el; node && node !== root; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (style.transform && style.transform !== "none") {
            try {
                const m = new DOMMatrixReadOnly(style.transform);
                if (m.a < 0) sign *= -1;
            } catch { /* ignore */ }
        }
        if (style.scale && style.scale !== "none") {
            const sx = parseFloat(style.scale.split(" ")[0]);
            if (sx < 0) sign *= -1;
        }
    }
    return sign;
}

function drawCover(ctx, img, width, height) {
    const scale = Math.max(width / img.naturalWidth, height / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
}

function drawWatermark(ctx, width, height) {
    const fontSize = Math.max(14, Math.round(width / 40));
    ctx.save();
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    const pad = fontSize * 0.8;
    const textWidth = ctx.measureText(WATERMARK).width;
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)";
    ctx.fillRect(width - textWidth - pad * 1.5, height - fontSize - pad * 1.3, textWidth + pad, fontSize + pad * 0.8);
    ctx.fillStyle = "#ffd700";
    ctx.fillText(WATERMARK, width - pad, height - pad * 0.9);
    ctx.restore();
}

/**
 * Records the DOM aquarium into a video by redrawing it onto a canvas every frame.
 * Resolves with { blob, mimeType }.
 */
export async function recordAquarium(aquariumEl, { durationMs = RECORD_DURATION_MS, onProgress } = {}) {
    if (!aquariumEl) throw new Error("Aquarium not ready");
    const mimeType = pickMimeType();
    if (mimeType === null) throw new Error("unsupported");

    const rootRect = aquariumEl.getBoundingClientRect();
    const scale = Math.min(1, MAX_WIDTH / rootRect.width);
    const width = Math.round(rootRect.width * scale / 2) * 2;
    const height = Math.round(rootRect.height * scale / 2) * 2;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    if (typeof canvas.captureStream !== "function") throw new Error("unsupported");
    const ctx = canvas.getContext("2d");

    const background = await loadImage(cssUrl(window.getComputedStyle(aquariumEl).backgroundImage));
    const sprites = new Map();
    const fishEls = () => Array.from(aquariumEl.querySelectorAll("img")).filter((img) => img.style.webkitMaskImage || img.style.WebkitMaskImage);
    await Promise.all(fishEls().map(async (el) => sprites.set(el.src, await prepareFishSprite(el))));

    const drawFrame = () => {
        const current = aquariumEl.getBoundingClientRect();
        ctx.fillStyle = "#0b4f7a";
        ctx.fillRect(0, 0, width, height);
        if (background) drawCover(ctx, background, width, height);
        for (const el of fishEls()) {
            const sprite = sprites.get(el.src);
            if (sprite === undefined) {
                sprites.set(el.src, null);
                prepareFishSprite(el).then((s) => sprites.set(el.src, s));
                continue;
            }
            if (!sprite) continue;
            const r = el.getBoundingClientRect();
            const x = (r.left - current.left) * scale;
            const y = (r.top - current.top) * scale;
            const w = r.width * scale;
            const h = r.height * scale;
            ctx.save();
            if (horizontalSign(el, aquariumEl) < 0) {
                ctx.translate(x + w, y);
                ctx.scale(-1, 1);
                ctx.drawImage(sprite, 0, 0, w, h);
            } else {
                ctx.drawImage(sprite, x, y, w, h);
            }
            ctx.restore();
        }
        drawWatermark(ctx, width, height);
    };

    drawFrame();
    const stream = canvas.captureStream(FPS);
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 4_000_000 } : undefined);
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

    return new Promise((resolve, reject) => {
        let rafId;
        const start = performance.now();
        const loop = () => {
            drawFrame();
            onProgress?.(Math.min(1, (performance.now() - start) / durationMs));
            rafId = requestAnimationFrame(loop);
        };
        recorder.onerror = (e) => { cancelAnimationFrame(rafId); reject(e.error || new Error("recording failed")); };
        recorder.onstop = () => {
            cancelAnimationFrame(rafId);
            stream.getTracks().forEach((track) => track.stop());
            const type = recorder.mimeType || mimeType || "video/webm";
            resolve({ blob: new Blob(chunks, { type }), mimeType: type });
        };
        recorder.start(250);
        loop();
        setTimeout(() => recorder.state !== "inactive" && recorder.stop(), durationMs);
    });
}

export async function shareOrDownload(blob, mimeType) {
    const fileName = `my-fishy-aquarium.${extensionFor(mimeType)}`;
    const file = typeof File === "function" ? new File([blob], fileName, { type: blob.type }) : null;
    if (file && navigator.canShare?.({ files: [file] }) && navigator.share) {
        try {
            await navigator.share({ files: [file], title: "My Fishy aquarium", text: "Look at our fish swimming! Made with Fishy – swimfishy.com" });
            return "shared";
        } catch (e) {
            if (e?.name === "AbortError") return "cancelled";
        }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return "downloaded";
}
